

export const stripHtml = (htmlString) => {
  if (!htmlString) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = htmlString;
  return tmp.textContent || tmp.innerText || "";
};

// Quill represents an empty line as a block containing only a <br>. Those
// blocks pick up the regular paragraph margin in the blog renderer, so they
// create (and on subsequent edits can accumulate) unexpectedly large gaps.
// Keep intentional line breaks inside text, but discard standalone blank
// blocks and root-level breaks; normal block spacing is handled by CSS.
export const normalizeRichTextHtml = (htmlString) => {
  if (!htmlString || typeof document === "undefined") return htmlString || "";

  const container = document.createElement("DIV");
  container.innerHTML = htmlString;

  [...container.childNodes].forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) node.remove();
    if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "BR") node.remove();
  });

  container.querySelectorAll("p, div").forEach((block) => {
    const text = (block.textContent || "").replace(/\u00a0/g, "").trim();
    const hasNonBreakContent = [...block.children].some((child) => child.tagName !== "BR");
    if (!text && !hasNonBreakContent) block.remove();
  });

  return container.innerHTML;
};


export const convertTextToUnderscore = (text) => {
  if (!text) return "none";
  return text
    .toLocaleLowerCase()
    .replace(/[^A-Z0-9\-]+/gi, "-")
    .substring(0, 50);
};


export const convertJsonToParams = (data) => {
  const parameters = [];
  Object.keys(data).map((k) => {
    if (Array.isArray(data[k])) {
      data[k].map((e) =>
        parameters.push(
          //eslint-disable-next-line
          encodeURIComponent(`${k}[]`) + "=" + encodeURIComponent(e || "") 
        )
      );
    } else if (data[k] && typeof data[k] === "object") {
      // console.log(data[k])
      Object.keys(data[k]).map((key1) =>
        parameters.push(
            //eslint-disable-next-line
            encodeURIComponent(`${k}[${key1}]`) + '=' + encodeURIComponent(data[k][key1] || "")
        )
      );
    } else {
      parameters.push(
        //eslint-disable-next-line
        encodeURIComponent(`${k}`) + '=' + encodeURIComponent(data[k] || "")
      );
    }
    return null;
  });
  return parameters.join("&");
};

// ==================================
// MAPs Utils
// ==================================
const parseStreet = (result, useShortName = true) => {
  const address_components = result.address_components;
  const streetNumber = address_components.find(({ types }) =>
    types.includes("street_number")
  );

  const streetName = address_components.find(({ types }) =>
    types.includes("route")
  );

  return [streetNumber, streetName]
    .filter((obj) => !!obj)
    .map((obj) => (useShortName ? obj.short_name : obj.long_name))
    .join(" ");
};

const parseStreet2 = (result, useShortName = true) => {
  const address_components = result.address_components;
  const subpremise = address_components.find(({ types }) =>
    types.includes("subpremise")
  );
  if (!subpremise) return null;
  return useShortName ? subpremise.short_name : subpremise.long_name;
};

const parseCity = (result, useShortName = true) => {
  const address_components = result.address_components;
  let city = address_components.find(
    ({ types }) => types.includes("locality") && types.includes("political")
  );
  if (!city) {
    const province = address_components.find(
      ({ types }) =>
        types.includes("administrative_area_level_1") &&
        types.includes("political")
    );
    if (province) {
      city = address_components.find(
        ({ types }) =>
          types.includes("administrative_area_level_2") &&
          types.includes("political")
      );
    }
  }

  if (!city) return null;

  return useShortName ? city.long_name : city.long_name;
};

const parseProvince = (result, useShortName = true) => {
  const address_components = result.address_components;
  let province = address_components.find(
    ({ types }) =>
      types.includes("administrative_area_level_1") &&
      types.includes("political")
  );
  if (!province) {
    province = address_components.find(
      ({ types }) =>
        types.includes("administrative_area_level_2") &&
        types.includes("political")
    );
  }

  if (!province) return null;

  return useShortName ? province.short_name : province.long_name;
};

const parsePostalCode = (result, useShortName = true) => {
  const address_components = result.address_components;
  const postalCode = address_components.find(({ types }) =>
    types.includes("postal_code")
  );
  if (!postalCode) return null;

  return useShortName ? postalCode.short_name : postalCode.long_name;
};

const parseCountry = (result, useShortName = true) => {
  const address_components = result.address_components;
  const country = address_components.find(
    ({ types }) => types.includes("country") && types.includes("political")
  );
  if (!country) return null;

  return useShortName ? country.short_name : country.long_name;
};

export const mapParser = {
  street: parseStreet,
  street2: parseStreet2,
  city: parseCity,
  province: parseProvince,
  postalCode: parsePostalCode,
  country: parseCountry,
};

export const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "var(--white10)",
    borderColor: state.isFocused ? "var(--red)" : "var(--border)",
    borderRadius: "6px",
    boxShadow: "none",
    "&:hover": {
      borderColor: state.isFocused ? "var(--red)" : "var(--border)",
    },
    minHeight: "40px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#1a1a1a",
    borderRadius: "6px",
    border: "1px solid var(--border)",
    zIndex: 999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "var(--red)" : "transparent",
    color: state.isFocused ? "var(--white)" : "var(--white70)",
    "&:active": {
      backgroundColor: "var(--red-dark)",
    },
    fontSize: "13px",
    cursor: "pointer",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "var(--red)",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "var(--white)",
    fontSize: "12px",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "var(--white)",
    "&:hover": {
      backgroundColor: "var(--red-dark)",
      color: "var(--white)",
    },
  }),
  input: (provided) => ({
    ...provided,
    color: "var(--white)",
    fontSize: "13px",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "var(--white)",
    fontSize: "13px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "var(--white40)",
    fontSize: "13px",
  }),
};
